import { useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError, API } from "@/lib/apiClient";
import { STANDARD_PLEDGES } from "@/lib/constants";
import { displayDoctorName } from "@/lib/utils";
import { fileToResizedDataUrl } from "@/lib/photo";
import { usePresence } from "@/lib/usePresence";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Download, CheckCircle2, Loader2, Camera, X } from "lucide-react";

const EMPTY = { name: "", reg_no: "", mobile: "", email: "", photo: null };

// Plain promotional pledge page (/pledge/<promo-link>): August Kranti banner
// on top, a short form (name / reg no / mobile / email / photo), the pledge
// chooser, and ONLY the promoted brand's creative at the bottom. No navbar,
// footer or other brands — designed to be shared as a campaign link.
export default function PromoPledgePage({ linkName, brandLogo }) {
  const [form, setForm] = useState(EMPTY);
  const [pledgeChoice, setPledgeChoice] = useState(STANDARD_PLEDGES[0].id);
  const [customPledge, setCustomPledge] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  usePresence(`promo:${linkName}`);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      set("photo", await fileToResizedDataUrl(file));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Enter your full name";
    if (form.reg_no.trim().length < 2) e.reg_no = "Enter your registration number";
    if (!/^\d{6,15}$/.test(form.mobile.replace(/\s+/g, ""))) e.mobile = "Enter a valid mobile number";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const chosenPledgeText =
    pledgeChoice === "custom"
      ? customPledge.trim()
      : STANDARD_PLEDGES.find((p) => p.id === pledgeChoice)?.text || "";

  const submit = async () => {
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    if (pledgeChoice === "custom" && customPledge.trim().length < 20) {
      toast.error("Please write your pledge (at least 20 characters).");
      return;
    }
    if (!agreed) { toast.error("Please confirm the pledge to continue."); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/pledges/via/${linkName}`, { ...form, pledge_text: chosenPledgeText });
      setResult(res.data);
      toast.success("Pledge submitted successfully!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const banner = (
    <div className="pt-6 pb-2 px-5 flex justify-center">
      <img src="/AugKranti.png" alt="August Kranti — For a Healthy India" className="w-full max-w-md h-auto" />
    </div>
  );

  const brandFooter = brandLogo && (
    <div data-testid="promo-brand-footer" className="mt-2 py-6 px-5 flex justify-center border-t border-slate-100">
      <img src={brandLogo} alt="Brand" className="max-h-40 w-auto max-w-full object-contain" />
    </div>
  );

  if (result) {
    return (
      <div data-testid="promo-success" className="min-h-screen bg-white flex flex-col">
        <div className="tricolor-bar h-1.5 w-full" />
        {banner}
        <div className="flex-1 max-w-xl mx-auto w-full px-5 py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-3xl bg-white border border-slate-200 p-8 text-center shadow-sm"
          >
            <div className="grid place-items-center w-16 h-16 rounded-full bg-green-50 mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9 text-[#138808]" />
            </div>
            <h1 className="font-display font-extrabold text-2xl text-slate-900">Thank you, {displayDoctorName(result.name)}!</h1>
            <p className="mt-2 text-slate-600">Your pledge has been recorded on the Healthy India Pledge Wall.</p>
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Certificate ID</p>
              <p data-testid="certificate-id" className="font-display font-bold text-xl text-[#0056D2]">{result.certificate_id}</p>
            </div>
            <a href={`${API}/certificate/${result.certificate_id}`} className="mt-6 inline-flex" data-testid="promo-cert-btn">
              <Button size="lg" className="rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold px-7 h-12">
                <Download className="w-4 h-4 mr-2" /> Download Certificate
              </Button>
            </a>
          </motion.div>
        </div>
        {brandFooter}
      </div>
    );
  }

  const field = (id, label, node) => (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="mt-1.5">{node}</div>
      {errors[id] && <p data-testid={`error-${id}`} className="text-xs text-red-600 mt-1">{errors[id]}</p>}
    </div>
  );

  return (
    <div data-testid="promo-pledge-page" className="min-h-screen bg-white flex flex-col">
      <div className="tricolor-bar h-1.5 w-full" />
      {banner}

      <div className="flex-1 max-w-6xl mx-auto w-full px-5 pb-4 grid lg:grid-cols-5 gap-6">
        {/* Short form */}
        <div className="lg:col-span-3 py-4">
          <h1 className="font-display font-extrabold text-3xl text-slate-900">Take the Pledge</h1>
          <p className="mt-1.5 text-slate-500">Registered medical practitioners only. All fields required.</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {field("name", "Full Name",
              <Input data-testid="input-name" id="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Dr. Full Name" />)}
            {field("reg_no", "Medical Council Reg. No.",
              <Input data-testid="input-reg_no" id="reg_no" value={form.reg_no} onChange={(e) => set("reg_no", e.target.value)} placeholder="e.g. MH/12345/2015" />)}
            {field("mobile", "Mobile Number",
              <Input data-testid="input-mobile" id="mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" />)}
            {field("email", "Email",
              <Input data-testid="input-email" id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />)}
          </div>

          <div className="mt-6">
            <Label className="text-sm font-medium text-slate-700">Your Photo <span className="text-slate-400 font-normal">(optional — shown on the live pledge wall)</span></Label>
            <div className="mt-2 flex items-center gap-4">
              {form.photo ? (
                <div className="relative">
                  <img src={form.photo} alt="Preview" data-testid="pledge-photo-preview" className="w-20 h-20 rounded-full object-cover ring-2 ring-[#FF9933]" />
                  <button
                    type="button"
                    data-testid="pledge-photo-remove"
                    onClick={() => set("photo", null)}
                    aria-label="Remove photo"
                    className="absolute -top-1 -right-1 grid place-items-center w-6 h-6 rounded-full bg-red-500 text-white shadow"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <label className="inline-flex items-center gap-2 rounded-full bg-[#0056D2] text-white text-sm font-semibold px-5 py-2.5 cursor-pointer hover:bg-blue-700 transition-colors">
                    <Camera className="w-4 h-4" /> Take Photo
                    <input data-testid="pledge-photo-capture" type="file" accept="image/*" capture="environment" className="sr-only" onChange={onPhotoChange} />
                  </label>
                  <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-600 text-sm font-semibold px-5 py-2.5 cursor-pointer hover:border-[#0056D2] hover:text-[#0056D2] transition-colors">
                    🖼️ Upload from Gallery
                    <input data-testid="pledge-photo-input" type="file" accept="image/*" className="sr-only" onChange={onPhotoChange} />
                  </label>
                </div>
              )}
              <p className="text-xs text-slate-500 max-w-[220px]">
                A clear front-facing photo works best. It appears on your card in the India map wall.
              </p>
            </div>
          </div>

          {/* Brand creative below the form, beside the pledge panel (desktop) */}
          {brandLogo && (
            <div data-testid="promo-brand-inline" className="hidden lg:flex mt-8 pt-6 border-t border-slate-100 justify-center">
              <img src={brandLogo} alt="Brand" className="max-h-44 w-auto max-w-full object-contain" />
            </div>
          )}
        </div>

        {/* Pledge chooser */}
        <div className="lg:col-span-2">
          <div className="rounded-3xl bg-slate-950 text-white p-6 h-full">
            <div className="tricolor-bar h-1 w-16 rounded-full mb-4" />
            <h2 className="font-display font-bold text-xl">Choose Your Pledge</h2>
            <p className="mt-1 text-xs text-slate-400">Pick a ready pledge or write your own.</p>

            <div className="mt-4 space-y-2.5" data-testid="pledge-options">
              {STANDARD_PLEDGES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`pledge-option-${p.id}`}
                  onClick={() => setPledgeChoice(p.id)}
                  className={`w-full text-left rounded-xl border p-3.5 transition ${
                    pledgeChoice === p.id ? "border-[#FF9933] bg-[#FF9933]/10" : "border-white/15 bg-white/5 hover:border-white/40"
                  }`}
                >
                  <p className={`text-xs font-bold uppercase tracking-wide ${pledgeChoice === p.id ? "text-[#FF9933]" : "text-slate-400"}`}>{p.title}</p>
                  <p className="mt-1 text-[13px] text-slate-200 leading-snug italic">“{p.text}”</p>
                </button>
              ))}
              <button
                type="button"
                data-testid="pledge-option-custom"
                onClick={() => setPledgeChoice("custom")}
                className={`w-full text-left rounded-xl border p-3.5 transition ${
                  pledgeChoice === "custom" ? "border-[#FF9933] bg-[#FF9933]/10" : "border-white/15 bg-white/5 hover:border-white/40"
                }`}
              >
                <p className={`text-xs font-bold uppercase tracking-wide ${pledgeChoice === "custom" ? "text-[#FF9933]" : "text-slate-400"}`}>
                  ✍️ Write my own pledge
                </p>
                {pledgeChoice === "custom" && (
                  <Textarea
                    data-testid="custom-pledge-input"
                    value={customPledge}
                    onChange={(e) => setCustomPledge(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    maxLength={1000}
                    rows={4}
                    placeholder="Type your pledge in English or your regional language…"
                    className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-slate-500 text-sm"
                  />
                )}
              </button>
            </div>

            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="pledge-agree-checkbox" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5 border-white/40 data-[state=checked]:bg-[#FF9933] data-[state=checked]:border-[#FF9933]" />
              <span className="text-sm text-slate-200">I read and take this pledge as a registered medical practitioner (self-declared).</span>
            </label>
            <Button
              data-testid="submit-pledge-button"
              onClick={submit}
              disabled={submitting}
              size="lg"
              className="mt-5 w-full rounded-full bg-[#FF9933] hover:bg-orange-500 text-slate-900 font-bold h-12"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "I Pledge"}
            </Button>
          </div>
        </div>
      </div>

      {/* On mobile the creative goes full-width at the bottom */}
      <div className="lg:hidden">{brandFooter}</div>
    </div>
  );
}
